'use client';

import { useState, useEffect } from 'react';

const DEVICE_ID_KEY = 'slidetoanki_device_id';

/**
 * Generates a UUID v4
 */
function generateUUID(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Hook that returns a persistent device ID.
 * Generates a UUID on first visit, stores in localStorage, and returns it on subsequent visits.
 * Returns null during SSR and while loading from localStorage.
 */
export function useDeviceId(): string | null {
  const [deviceId, setDeviceId] = useState<string | null>(null);

  useEffect(() => {
    // Check localStorage for existing device ID
    let storedId = localStorage.getItem(DEVICE_ID_KEY);
    
    if (!storedId) {
      // Generate new UUID and store it
      storedId = generateUUID();
      localStorage.setItem(DEVICE_ID_KEY, storedId);
    }
    
    setDeviceId(storedId);
  }, []);

  return deviceId;
}
